import { useTableViewHelpers } from '@design-system/molecules/ticlick-table/hooks/use-table-view-helpers';
import type { TSimpleFilterConfig } from '../../../types/filter-config';
import { FilterOptionItem } from '@src/design-system/filter-new';
import {
  FILTER_NONE_LABEL,
  FILTER_NONE_VALUE,
  oppositeBooleanToken,
} from '../../../helpers/filter-none';
import { useShowsNoneOption } from '../../../helpers/use-shows-none-option';

export function SimpleFilterPanel({ config }: { config: TSimpleFilterConfig }) {
  const { setFacetValue, getSelectedStaticFilterFacet } = useTableViewHelpers();
  const showNoneOption = useShowsNoneOption(config.withNoneOption);

  const facet = getSelectedStaticFilterFacet(config.facetName);
  const selectedValues = facet?.value || [];

  const toggle = (val: string | null) => {
    const keepsNoneWithOtherValues = !config.booleanValues;

    if (val === null) {
      const isSelected = selectedValues.includes(FILTER_NONE_VALUE);
      setFacetValue({
        field: config.facetName,
        value: keepsNoneWithOtherValues
          ? isSelected
            ? selectedValues.filter((current) => current !== FILTER_NONE_VALUE)
            : [...selectedValues, FILTER_NONE_VALUE]
          : isSelected
            ? []
            : [FILTER_NONE_VALUE],
      });
      return;
    }

    const isSelected = selectedValues.includes(val);
    const opposite = config.booleanValues ? oppositeBooleanToken(val) : null;
    const remaining = selectedValues.filter((current) => {
      if (current === val || current === opposite) return false;
      if (!keepsNoneWithOtherValues && current === FILTER_NONE_VALUE) {
        return false;
      }
      return true;
    });
    const value = isSelected ? remaining : [...remaining, val];

    setFacetValue({
      field: config.facetName,
      value,
      ...(config.friendlyField && {
        friendlyField: config.friendlyField,
        friendlyValue: value.map(
          (v) =>
            config.options.find((opt) => opt.value === v)?.label ?? String(v)
        ),
      }),
    });
  };

  return (
    <div className='max-h-52 p-2 overflow-y-auto scrollbar-gray'>
      {config.options.map((opt) => (
        <FilterOptionItem
          key={opt.value}
          onClick={() => toggle(opt.value)}
          selected={selectedValues.includes(opt.value)}
        >
          {opt.renderLabel ?? opt.label}
        </FilterOptionItem>
      ))}
      {showNoneOption ? (
        <FilterOptionItem
          onClick={() => toggle(FILTER_NONE_VALUE)}
          selected={selectedValues.includes(FILTER_NONE_VALUE)}
        >
          {FILTER_NONE_LABEL}
        </FilterOptionItem>
      ) : null}
    </div>
  );
}
